import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Select from 'react-select';
import menuPortalProps from 'helpers/reactSelectPortal';
import AsyncSelect from 'react-select/async';
import { createApprovalFlow, updateApprovalFlow } from 'redux/booking/travel/setting/approvalFlowSlice';
import { getActiveSites } from 'redux/organization/siteSlice';
import { activeEmployeesByNameDebounce } from 'helpers/employeeOptionHelper';
import { Drawer, Button, FormRow } from 'components/common';

const ApprovalFlowForm = ({ showModal, setshowModal, title, record }) => {
  const { control, handleSubmit, formState: { errors }, setValue } = useForm();
  const dispatch = useDispatch();
  const { activeData: siteData } = useSelector((state) => state.organization_site);

  useEffect(() => { dispatch(getActiveSites()); }, [dispatch]);

  useEffect(() => {
    if (record) Object.entries(record).forEach(([key, value]) => setValue(key, value));
  }, [setValue, record]);

  const closeModal = () => setshowModal(false);

  const onSubmit = (data) => {
    try {
      if (record && record.id) dispatch(updateApprovalFlow({ approvalId: record.id, data }));
      else dispatch(createApprovalFlow(data));
      closeModal();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const sitesOptions = (siteData || []).map((item) => ({ value: item.id, label: item.name }));
  const defaultSiteOption = useMemo(() => (record ? { value: record?.site_id, label: record?.site_name } : null), [record]);
  const empOptions = (inputValue, callback) => activeEmployeesByNameDebounce(inputValue, callback);

  // Multi-value default (array of names) → react-select option list.
  const multiDefault = (key) => (record && Array.isArray(record[key]) ? record[key].map((o) => ({ value: o, label: o })) : null);
  // Single-value default.
  const singleDefault = (key) => (record && record[key] ? { value: record[key], label: record[key] } : null);
  // Multi onChange → store array of values under the matching field.
  const setMulti = (field) => (opts) => setValue(field, (opts || []).map((o) => o.value), { shouldValidate: true });
  const setSingle = (field) => (opt) => setValue(field, opt ? opt.value : null);

  const asyncMulti = (name, placeholder) => (
    <Controller control={control} name={name} rules={{ required: { value: true, message: `${placeholder} is required` } }}
      render={() => (
        <AsyncSelect cacheOptions defaultOptions defaultValue={multiDefault(name)} loadOptions={empOptions} closeMenuOnSelect placeholder={placeholder} classNamePrefix="react-select" {...menuPortalProps} isMulti onChange={setMulti(name)} />
      )} />
  );
  const asyncSingle = (name, placeholder, required = true, clearable = false) => (
    <Controller control={control} name={name} rules={required ? { required: { value: true, message: `${placeholder} is required` } } : {}}
      render={() => (
        <AsyncSelect cacheOptions defaultOptions defaultValue={singleDefault(name)} loadOptions={empOptions} closeMenuOnSelect placeholder={placeholder} classNamePrefix="react-select" {...menuPortalProps} isClearable={clearable} onChange={setSingle(name)} />
      )} />
  );

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title={title}
      footer={(
        <>
          <Button size="sm" variant="secondary" onClick={closeModal}>Cancel</Button>
          <Button size="sm" type="submit" form="approval-flow-form" variant="primary">Save</Button>
        </>
      )}
    >
      <form id="approval-flow-form" onSubmit={handleSubmit(onSubmit)}>
        <FormRow label="Site" error={errors.site_id?.message}>
          <Controller control={control} name="site_id" rules={{ required: 'Site is required' }}
            render={() => (
              <Select closeMenuOnSelect options={sitesOptions} defaultValue={defaultSiteOption} placeholder="Select..." classNamePrefix="react-select" {...menuPortalProps}
                onChange={(opt) => setValue('site_id', opt.value, { shouldValidate: true })} />
            )} />
        </FormRow>

        <FormRow label="HR admin (site office)" error={errors.hr_admins_site_office_confirm?.message}>
          {asyncMulti('hr_admins_site_office_confirm', 'HR admin')}
        </FormRow>

        <FormRow label="Chief Rep/GM" error={errors.chief_rep_gm_approval?.message}>
          {asyncSingle('chief_rep_gm_approval', 'Chief Rep/GM')}
        </FormRow>

        <FormRow label="FNC Check" error={errors.accountants_confirm?.message}>
          {asyncMulti('accountants_confirm', 'FNC Check')}
        </FormRow>

        <FormRow label="C&B Check" error={errors.c_b_verify?.message}>
          {asyncMulti('c_b_verify', 'C&B Check')}
        </FormRow>

        <FormRow label="HR Admin head office" error={errors.hr_admins_head_office_confirm?.message}>
          {asyncMulti('hr_admins_head_office_confirm', 'HR Admin head office')}
        </FormRow>

        <FormRow label="HR manager head office" error={errors.hr_manager_approval?.message}>
          {asyncSingle('hr_manager_approval', 'HR manager head office')}
        </FormRow>

        <FormRow label="General Director approval" error={errors.gd_approval?.message}>
          {asyncSingle('gd_approval', 'General Director approval', false, true)}
        </FormRow>
      </form>
    </Drawer>
  );
};

ApprovalFlowForm.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  record: PropTypes.object.isRequired,
};

export default ApprovalFlowForm;
